import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './ReservationEditForm.css';
import { getApiUrl } from '../utils/api';
import { useAutoFillUserInfo } from '../utils/useAutoFillUserInfo';

/**
 * Gets the ID token from MSAL instance
 * @param {Object} msalInstance - MSAL instance
 * @returns {Promise<string|null>} - ID token or null if unavailable
 */
async function getIdToken(msalInstance) {
  try {
    const accounts = msalInstance.getAllAccounts();
    if (!accounts || accounts.length === 0) {
      return null;
    }
    const account = accounts[0];
    const response = await msalInstance.acquireTokenSilent({
      scopes: ['User.Read'],
      account: account,
    });
    return response.idToken || null;
  } catch (error) {
    console.error('Failed to get ID token:', error);
    return null;
  }
}

function ReservationEditForm({ 
  reservation, 
  date, 
  slotLabel, 
  parkingSlots, 
  releases,
  slot,
  onClose, 
  onSave, 
  msalInstance 
}) {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({
    date: '',
    slot_id: '',
    user_id: '',
    user_name: '',
    family_name: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Hook to auto-fill user_name and family_name from user_id/email
  const handleUserIdBlur = useAutoFillUserInfo(formData, setFormData);

  // Initialize form data
  useEffect(() => {
    if (reservation) {
      // Editing existing reservation
      const dateStr = date instanceof Date 
        ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
        : (typeof date === 'string' ? date : '');
      setFormData({
        date: dateStr,
        slot_id: slotLabel,
        user_id: reservation.user_id || '',
        user_name: reservation.user_name || '',
        family_name: reservation.family_name || '',
      });
    } else {
      // Creating new reservation (empty slot clicked by PowerUser)
      const dateStr = date instanceof Date 
        ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
        : (typeof date === 'string' ? date : '');
      setFormData({
        date: dateStr,
        slot_id: slotLabel || '',
        user_id: '',
        user_name: '',
        family_name: '',
      });
    }
  }, [reservation, date, slotLabel]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const idToken = await getIdToken(msalInstance);
      if (!idToken) {
        throw new Error(t('reservationForm.unableToGetToken'));
      }

      // Validate required fields
      if (!formData.date || !formData.slot_id || !formData.user_id) {
        throw new Error(t('reservationForm.dateSlotUserIdRequired'));
      }

      // If editing, use PUT; if creating, use POST
      const method = reservation ? 'PUT' : 'POST';
      const url = getApiUrl('/api/reservations');

      const payload = {
        date: formData.date,
        slot_id: formData.slot_id,
        user_id: formData.user_id,
        user_name: formData.user_name || formData.user_id.split('@')[0],
        family_name: formData.family_name || '',
      };

      // If creating, add action field required by POST endpoint
      if (!reservation) {
        payload.action = 'create';
      } else {
        // If editing, include original date and slot_id for identification
        payload.original_date = date instanceof Date 
          ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
          : (typeof date === 'string' ? date : '');
        payload.original_slot_id = slotLabel;
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || t('reservationForm.failedToSave'));
      }

      // Call onSave callback to refresh data
      if (onSave) {
        await onSave();
      }

      // Close the form
      onClose();
    } catch (e) {
      console.error('Failed to save reservation:', e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!reservation) return;

    const confirmed = window.confirm(
      t('reservationForm.deleteConfirm', {
        slot: slotLabel,
        date: formData.date,
        user: formData.user_name || formData.user_id
      })
    );

    if (!confirmed) return;

    setError(null);
    setLoading(true);

    try {
      const idToken = await getIdToken(msalInstance);
      if (!idToken) {
        throw new Error(t('reservationForm.unableToGetToken'));
      }

      const dateStr = date instanceof Date 
        ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
        : (typeof date === 'string' ? date : '');

      const response = await fetch(getApiUrl('/api/reservations'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          action: 'delete',
          date: dateStr,
          slot_id: slotLabel,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || t('reservationForm.failedToDelete'));
      }

      // Call onSave callback to refresh data
      if (onSave) {
        await onSave();
      }

      // Close the form
      onClose();
    } catch (e) {
      console.error('Failed to delete reservation:', e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReleaseSlot = async () => {
    if (!slot || !slot.default_owner) return;

    const dateStr = formData.date || (date instanceof Date 
      ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
      : (typeof date === 'string' ? date : ''));
    
    const isReleased = releases?.[dateStr]?.[slotLabel];
    const action = isReleased ? 'delete' : 'create';
    const actionText = isReleased ? t('reservationForm.claimBackSlot') : t('reservationForm.releaseSlot');
    const effectText = isReleased ? t('reservationForm.releaseEffect') : t('reservationForm.claimEffect');

    const confirmed = window.confirm(
      t('reservationForm.releaseConfirm', {
        action: actionText.toLowerCase(),
        slot: slotLabel,
        date: dateStr,
        owner: slot.default_owner,
        effect: effectText
      })
    );

    if (!confirmed) return;

    setError(null);
    setLoading(true);

    try {
      const idToken = await getIdToken(msalInstance);
      if (!idToken) {
        throw new Error(t('reservationForm.unableToGetToken'));
      }

      const response = await fetch(getApiUrl('/api/slotReleases'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          action,
          date: dateStr,
          slot_id: slotLabel,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || t('reservationForm.failedToRelease', { action: actionText.toLowerCase() }));
      }

      // Call onSave callback to refresh data
      if (onSave) {
        await onSave();
      }

      // Don't close the form, just refresh the data
    } catch (e) {
      console.error(`Failed to ${actionText} slot:`, e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="reservation-edit-modal-overlay" onClick={onClose}>
      <div className="reservation-edit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="reservation-edit-modal-header">
          <h2>{reservation ? t('reservationForm.editReservation') : t('reservationForm.createReservation')}</h2>
          <button className="reservation-edit-modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        {error && (
          <div className="reservation-edit-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="reservation-edit-form">
          <div className="form-group">
            <label htmlFor="date">
              Date <span className="required">*</span>
            </label>
            <input
              type="date"
              id="date"
              lang={i18n.language === 'fi' ? 'fi' : 'en-GB'}
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              required
            />
            <small className="form-hint">Select the reservation date</small>
          </div>

          <div className="form-group">
            <label htmlFor="slot_id">
              Parking Slot <span className="required">*</span>
            </label>
            <select
              id="slot_id"
              value={formData.slot_id}
              onChange={(e) => setFormData({ ...formData, slot_id: e.target.value })}
              required
            >
              <option value="">Select a slot</option>
              {parkingSlots.map((slot) => (
                <option key={slot.id} value={slot.label}>
                  {slot.label} ({slot.location === 'basement' ? 'Basement' : 'Ground Floor'})
                </option>
              ))}
            </select>
            <small className="form-hint">Select the parking slot</small>
          </div>

          <div className="form-group">
            <label htmlFor="user_id">
              User ID (Email) <span className="required">*</span>
            </label>
            <input
              type="email"
              id="user_id"
              value={formData.user_id}
              onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
              onBlur={handleUserIdBlur}
              placeholder="user.name@example.com"
              required
            />
            <small className="form-hint">Azure AD username/email address</small>
          </div>

          <div className="form-group">
            <label htmlFor="user_name">Full Name</label>
            <input
              type="text"
              id="user_name"
              value={formData.user_name}
              onChange={(e) => setFormData({ ...formData, user_name: e.target.value })}
              placeholder={t('reservationForm.fullNamePlaceholder')}
            />
            <small className="form-hint">{t('reservationForm.fullNameHint')}</small>
          </div>

          <div className="form-group">
            <label htmlFor="family_name">{t('reservationForm.lastName')}</label>
            <input
              type="text"
              id="family_name"
              value={formData.family_name}
              onChange={(e) => setFormData({ ...formData, family_name: e.target.value })}
              placeholder={t('reservationForm.lastNamePlaceholder')}
            />
            <small className="form-hint">{t('reservationForm.lastNameHint')}</small>
          </div>

          {slot && slot.default_owner && (
            <div className="form-group" style={{ marginTop: '1rem', padding: '1rem', background: '#f3f4f6', borderRadius: '0.5rem' }}>
              <label style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>
                {t('reservationForm.slotReleaseManagement')}
              </label>
              <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.75rem' }}>
                {t('reservationForm.defaultOwner', { owner: slot.default_owner })}
                {releases?.[formData.date]?.[slotLabel] && (
                  <span style={{ color: '#10b981', marginLeft: '0.5rem' }}>{t('reservationForm.released')}</span>
                )}
              </div>
              <button 
                type="button" 
                onClick={handleReleaseSlot} 
                className="btn-release" 
                disabled={loading}
                style={{
                  padding: '0.5rem 1rem',
                  background: releases?.[formData.date]?.[slotLabel] ? '#10b981' : '#f59e0b',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1,
                }}
              >
                {releases?.[formData.date]?.[slotLabel] ? t('reservationForm.claimBackSlot') : t('reservationForm.releaseSlot')}
              </button>
              <small className="form-hint" style={{ display: 'block', marginTop: '0.5rem' }}>
                {releases?.[formData.date]?.[slotLabel] 
                  ? t('reservationForm.releasedHint')
                  : t('reservationForm.releaseHint')}
              </small>
            </div>
          )}

          <div className="form-actions">
            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? t('reservationForm.saving') : (reservation ? t('reservationForm.updateReservation') : t('reservationForm.createReservation'))}
            </button>
            {reservation && (
              <button type="button" onClick={handleDelete} className="btn-delete" disabled={loading}>
                {t('reservationForm.deleteReservation')}
              </button>
            )}
            <button type="button" onClick={onClose} className="btn-cancel" disabled={loading}>
              {t('common.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ReservationEditForm;
